<template>
  <section class="license-details">

      <Breadcrumbs
        :breadcrumbs="breadcrumbs"
      >
        <div slot="breadcrumbAfter" class="d-flex align-items-center">
          <va-dropdown
            v-if="canUpdateLicense"
            class="custom-dropdown"
            fixed>
            <span slot="anchor">
              <slot>
                <div class="pl-3 pr-2 clickable">
                  <span class="fa fa-ellipsis-v" style="font-size: 1.5rem;"></span>
                </div>
              </slot>
            </span>

            <div class="contact-dropdown__content pl-3 pr-2 pt-2 pb-2">
              <!-- Edit License -->
              <div
                class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                @click.prevent="isUpdatingLicense = true"
              >
                <i class="fa fa-pencil"></i>&nbsp;
                <span class="pl-2">{{$t('people.actions.edit')}}</span>
              </div>

              <!-- Renew License -->
              <div
                class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                @click.prevent="onClickRenewLicense"
              >
                <i class="fa fa-pencil"></i>&nbsp;
                <span class="pl-2">{{$t('licenses.renew')}}</span>
              </div>
              <!-- Cancel License -->
              <div
                class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
                v-if="!isCanceledLicense"
                @click.prevent="onClickCancelLicense"
              >
                <i class="fa fa-times "></i>&nbsp;
                <span class="pl-2">{{$t('licenses.actions.cancel_license')}}</span>
              </div>
            </div>
          </va-dropdown>
        </div>
      </Breadcrumbs>

      <FadeTransition
        :duration="250"
        mode="out-in"
      >
        <div v-if="selectedLicense" >

          <div class="d-flex justify-content-start">
            <h4 class="mb-0">{{$t('kiosks.license_data')}}</h4>
          </div>

          <LicenseInfo
            :license="selectedLicense" />

          <Payments
            class="mt-5"
            :license="selectedLicense"
            @paymentCreated="refreshLicense()"
            @paymentUpdated="refreshLicense()"
            @paymentDestroyed="refreshLicense()" />

        </div>

        <div v-else class="loading-widget text-center d-flex flex-column justify-content-center">
          <h6><b>{{$t('tables.loading')}}</b></h6>
          <LoadingAnimation :size="'5px'" class="d-flex justify-content-center pt-4 pb-4"></LoadingAnimation>
        </div>

      </FadeTransition>

      <CreateOrUpdate
        v-if="isUpdatingLicense"
        :license="isRenewingLicense ? renewedLicense : selectedLicense"
        :isRenew="isRenewingLicense"
        @updated="onLicenseUpdated"
        @destroyed="onLicenseDestroyed"
        @renewed="onLicenseRenewed"
        @modalClosed="onLicenseModalWasClosed" />

      <ConfirmationModal
        v-if="showCancelLicenseModal"
        :title="$t('licenses.cancel')"
        :body="$t('licenses.cancel_message')"
        @modal-confirmed="cancelLicense"
        @modal-canceled="cancelLicenseCanceled" />
  </section>
</template>

<script>
import Breadcrumbs from '@/components/common/Breadcrumbs'
import FadeTransition from '@/components/common/transitions/FadeTransition'
import { LicensesService } from '@/api/LeadsService'
import LoadingAnimation from '@/components/users/components/Helpers/LoadingAnimation'
import CreateOrUpdate from './CreateOrUpdate'
import Payments from '@/components/licenses/payments/index'
import LicenseInfo from '@/components/licenses/LicenseInfo'
import ConfirmationModal from "../common/ConfirmationModal";

const licenseInfoType = 300
const WriteAccessType = 2
export default {
  components: {
    ConfirmationModal,
    LicenseInfo,
    Breadcrumbs,
    FadeTransition,
    LoadingAnimation,
    CreateOrUpdate,
    Payments
  },
  computed: {
    leadsService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollead_server)
    },
    hasWriteLicensePermissions: function () {
      return this.leadsService.user_permissions.some(permission => {
        return permission.info_type === licenseInfoType && permission.access_type >= WriteAccessType
      })
    },
    canUpdateLicense: function () {
      return this.hasWriteLicensePermissions && this.selectedLicense
    },
    isCanceledLicense: function () {
      return this.hasWriteLicensePermissions && this.selectedLicense.canceled_at !== null
    },
    breadcrumbs: function () {
      return [
        { title: this.$t('menu.audiology'), disabled: true },
        { title: this.$t('menu.kiosks'), meta: { type: 'function', value: () => this.$router.push({ name: `${this.leadsService.slug}licenses/kiosks`}) } },
        { title: this.selectedLicense ? this.selectedLicense.kiosk.name : null, meta: { type: 'function', value: () => this.$router.push({ name: `${this.leadsService.slug}licenses/kiosks/id`, params: { id: this.selectedLicense.kiosk.id } }) } },
        { title: this.selectedLicense ? this.selectedLicense.name : null, active: true }
      ]
    }
  },
  data: function () {
    return {
      showCancelLicenseModal: false,
      isUpdatingLicense: false,
      isRenewingLicense: false,
      selectedLicense: undefined,
      renewedLicense: undefined
    }
  },
  methods: {
    onClickRenewLicense: function () {
      this.isRenewingLicense = true
      let license = JSON.parse(JSON.stringify(this.selectedLicense))
      this.renewedLicense = Object.assign(license, {
        start_date: this.moment(license.end_date).format('YYYY-MM-DD'),
        end_date: this.moment(license.end_date).add(1, 'year').format('YYYY-MM-DD'),
        invoice_amount: null,
        invoice_date: null,
        invoice_number: null
      })
      this.isUpdatingLicense = true
    },
    onLicenseRenewed: function (license) {
      this.isUpdatingLicense = false
      this.isRenewingLicense = false
      window.history.pushState({}, '', `#/audiology/licenses/${license.id}`)
      this.selectLicense(license.id)
    },
    refreshLicense: function () {
      this.selectLicense(this.selectedLicense.id)
    },
    selectLicense: function (licenseId) {
      LicensesService.get(licenseId)
        .then(license => {
          this.selectedLicense = license
        })
        .catch(error => {
          console.log(error)
          this.$router.go(-1)
        })
    },
    redirectBack: function () {
      this.$router.go(-1)
    },
    onLicenseUpdated: function (license) {
      this.isUpdatingLicense = false
      this.isRenewingLicense = false
      this.selectedLicense = Object.assign(this.selectedLicense, license)
    },
    onLicenseDestroyed: function () {
      this.isUpdatingLicense = false
      setTimeout(() => {
        this.$router.go(-1)
      }, 250)
    },
    onLicenseModalWasClosed: function () {
      this.isUpdatingLicense = false
      this.isRenewingLicense = false
    },
    onClickCancelLicense: function() {
      this.showCancelLicenseModal = true
    },
    cancelLicense: function () {
      LicensesService.cancel(this.selectedLicense.id)
        .then((license) => {
          this.showToast(this.$t('licenses.states.canceled'), this.$store.state.successToastOptions)
          this.showCancelLicenseModal = false
          this.selectedLicense = Object.assign(this.selectedLicense, license)
        }).catch(errors => {
        console.log(errors)
      })
    },
    cancelLicenseCanceled: function () {
      this.showCancelLicenseModal = false
    },
  },
  beforeRouteEnter (to, from, next) {
    if (to.params.id !== undefined) {
      next(vm => {
        vm.selectLicense(parseInt(to.params.id))
        vm.from = from
      })
    } else {
      next(from)
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params.id !== undefined) {
      this.selectLicense(parseInt(to.params.id))
      next(from)
    } else {
      next(from)
    }
  },
}
</script>

<style scoped lang="scss">

.kiosk-link {
  color: #29c4a9;
  cursor: pointer;
}

.kiosk-link:hover{
  color: #47d9bf;
}

.kiosk-tag {
  color: #29c4a9;
  text-align: center;
  background: white;
  border-radius: 30px;
  margin-right: 0.65rem;
  padding: 0.5rem 0.5rem;
  font-size: 13.5px;
  cursor: pointer;
  border: solid 1.5px;
}

.kiosk-tag:hover {
  background: #29c4a9;
  color:white;
}

.medium-card {
  width: 48.75%;
}

.medium-card-right {
  margin-left: 1.25%;
}

.medium-card-left {
  margin-right: 1.25%;
}

@media screen and (max-width: 768px) {

  .medium-card {
    width: 100%;
  }

  .mardium-card-left {
    margin-left: 0;
  }

  .medium-card-right {
    margin-right: 0;
  }
}

.loading-widget {
  min-height: 60vh;
}

.text-success {
  color: #14be33 !important;
}

.license-details{
  overflow-x: hidden;
}
</style>
